import { Link } from 'react-router';
import {
  useListPublicHunts,
  type HuntSummary,
} from '@cdf/api-client-react';
import {
  TrophyIcon,
  ListOrderedIcon,
  ArrowRightIcon,
  AlertCircleIcon,
  SparklesIcon,
  BoxesIcon,
  SmartphoneIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

function HuntStatusBadge({ status }: { status: HuntSummary['status'] }) {
  if (status === 'active') {
    return (
      <Badge className="bg-green-600 text-white hover:bg-green-600">Active</Badge>
    );
  }
  if (status === 'closed') {
    return <Badge variant="outline">Closed</Badge>;
  }
  return <Badge variant="secondary">{status}</Badge>;
}

function HuntCard({ hunt }: { hunt: HuntSummary }) {
  const isActive = hunt.status === 'active';

  return (
    <Card className="group relative overflow-hidden transition-shadow hover:shadow-md">
      <CardContent className="flex h-full flex-col gap-4 p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
            <TrophyIcon className="h-5 w-5" aria-hidden />
          </div>
          <HuntStatusBadge status={hunt.status} />
        </div>

        <div className="flex flex-1 flex-col gap-1.5">
          <h3 className="text-lg font-semibold leading-snug">{hunt.name}</h3>
          {hunt.description ? (
            <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
              {hunt.description}
            </p>
          ) : (
            <p className="text-sm italic text-muted-foreground">No description provided.</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="font-normal">
            <SparklesIcon className="mr-1 h-3 w-3" aria-hidden />
            {hunt.completionBonusPoints} bonus pts
          </Badge>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {isActive ? (
            <Button asChild size="sm" className="w-full sm:w-auto">
              <Link to={`/hunt/${hunt.slug}`}>
                Participate in Hunt
                <ArrowRightIcon className="ml-1.5 h-3.5 w-3.5" aria-hidden />
              </Link>
            </Button>
          ) : (
            <Button asChild size="sm" variant="secondary" className="w-full sm:w-auto">
              <Link to={`/hunt/${hunt.slug}`}>
                View Hunt
                <ArrowRightIcon className="ml-1.5 h-3.5 w-3.5" aria-hidden />
              </Link>
            </Button>
          )}
          <div className="flex gap-2">
            <Button asChild size="sm" variant="outline" className="flex-1 sm:flex-none">
              <Link to={`/events/${hunt.slug}/twin`}>
                <BoxesIcon className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                3D Twin
              </Link>
            </Button>
            <Button asChild size="sm" variant="outline" className="flex-1 sm:flex-none">
              <Link to="/ar">
                <SmartphoneIcon className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                AR / CV
              </Link>
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 border-t border-border pt-3">
          <Button asChild size="sm" variant="ghost" className="text-muted-foreground">
            <Link to={`/hunt/${hunt.slug}/leaderboard/live`}>
              <ListOrderedIcon className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Live Leaderboard
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function HuntCardSkeleton() {
  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-6">
        <div className="flex items-start justify-between gap-3">
          <Skeleton className="h-11 w-11 rounded-lg" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
        <Skeleton className="h-5 w-28 rounded-full" />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Skeleton className="h-9 w-full sm:w-40" />
          <Skeleton className="h-9 w-full sm:w-36" />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Public discovery list of active treasure hunts for the /for/treasure-hunt landing page.
 */
export function PublicHuntsList() {
  const { data, isLoading, isError, refetch, isFetching } = useListPublicHunts({
    status: 'active',
  });

  const hunts = data?.hunts ?? [];

  return (
    <section
      id="available-hunts"
      className="bg-muted/30"
      aria-labelledby="available-hunts-heading"
    >
      <div className="mx-auto max-w-5xl px-4 py-16 md:py-20">
        <div className="mb-10 text-center">
          <h2
            id="available-hunts-heading"
            className="text-2xl font-bold tracking-tight sm:text-3xl"
          >
            Available Treasure Hunts
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-muted-foreground">
            Join a live hunt, earn points at stations, and climb the leaderboard.
          </p>
        </div>

        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2">
            <HuntCardSkeleton />
            <HuntCardSkeleton />
          </div>
        ) : isError ? (
          <div className="mx-auto flex max-w-md flex-col items-center gap-4 rounded-xl border border-border bg-background px-6 py-10 text-center">
            <AlertCircleIcon className="h-10 w-10 text-destructive" aria-hidden />
            <div className="space-y-1">
              <p className="font-semibold">Couldn&rsquo;t load hunts</p>
              <p className="text-sm text-muted-foreground">
                Something went wrong while fetching available treasure hunts.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              {isFetching ? 'Retrying…' : 'Try again'}
            </Button>
          </div>
        ) : hunts.length === 0 ? (
          <div className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-xl border border-dashed border-border bg-background px-6 py-10 text-center">
            <TrophyIcon className="h-10 w-10 text-muted-foreground" aria-hidden />
            <div className="space-y-1">
              <p className="font-semibold">No active hunts available</p>
              <p className="text-sm text-muted-foreground">
                Check back soon — new event hunts appear here when they go live.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2">
            {hunts.map((hunt) => (
              <HuntCard key={hunt.id} hunt={hunt} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
